/*
  rbc-gallery-open.js — the fork in the opening and the models wall (flagship hub template).
  Port of Wall() + forks() in research/design/flagship-hubs-2026-10/concepts/lookbook-gallery/prototype-lite/index.html.
  Contract (BUILD-CONTRACT.md §5): the wall owns the state { disc: '' | '<Tag>', fin: 'plain' | 'decorated' };
  it announces every change on document as `rbc:gallery:state` (its initial state once, with init: true), obeys
  `rbc:gallery:set`, and keeps the hash (#jumping, #dressage, #decorated, #dressage-decorated) with replaceState.
  On load: disc from ?filter.p.m.rbc.category if present, else the hash; fin from the hash. #models = All / Plain.
  Late listeners can read window.rbcGalleryOpen.state.
*/
(() => {
  'use strict';
  if (window.rbcGalleryOpen) { window.rbcGalleryOpen.boot(); return; }

  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const RM = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
  const sstep = (a, b, t) => { t = clamp((t - a) / (b - a)); return t * t * (3 - 2 * t); };
  const expo = t => t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
  const FILTER = 'filter.p.m.rbc.category';
  const PLAIN = 'plain', DEC = 'decorated';
  const nav = () => { try { return performance.getEntriesByType('navigation')[0] || {}; } catch (e) { return {}; } };
  const dclDone = () => document.readyState === 'complete' || nav().domContentLoadedEventStart > 0;
  const walls = new Map(); // root element → instance
  let last = null; // the most recent wall, for window.rbcGalleryOpen.state

  function Wall(root) {
    const ac = new AbortController(), sig = { signal: ac.signal };
    const wall = $('.go-wall', root), track = $('.go-track', root), ind = $('.go-ind', root), win = $('.go-ind-win', root);
    const live = $('.go-live', root), sum = $('.go-sum', root);
    let SUMS = {};
    try { SUMS = JSON.parse(($('.go-data', root) || {}).textContent || '{}').sums || {}; } catch (e) {}
    const items = $$('.go-it', track).map(el => ({ el, plate: $('.go-plate', el), img: $('img', el), discs: (el.dataset.disc || '').split('|').filter(Boolean), dec: el.hasAttribute('data-dec'), k: null }));
    const groups = $$('.go-wg', root);
    const BR = $$('[data-axis="disc"] .go-way', root).filter(b => b.dataset.v).map(b => ({ tag: b.dataset.v, key: b.dataset.h }));
    const hasFin = !!$('[data-axis="fin"]', root);
    const state = { disc: '', fin: PLAIN };
    const want = (it, s = state) => (s.disc === '' || it.discs.includes(s.disc)) && it.dec === (s.fin === DEC); // a boot tagged with no branch (Polo) shows under All
    let vis = items.filter(it => want(it)), focus = 0, focal = null, hoverIt = null, raf = 0, anim = null, suppress = false, gen = 0;
    let fading = [], entering = [];

    /* ---- geometry and the focal scaling (unchanged from the prototype) ---- */
    const geo = () => { const pad = parseFloat(getComputedStyle(track).paddingLeft) || 0; const iw = vis[0] ? vis[0].el.offsetWidth : 196; const vw = track.clientWidth; const Mx = Math.max(1, Math.min(track.scrollWidth - vw, pad * 2 + vis.length * iw - vw)); return { pad, iw, vw, Mx, b: Math.max(0, vw - 2 * pad - iw) }; };
    const focalOf = s => { const g = geo(); return s + g.pad + g.iw / 2 + (s / g.Mx) * g.b; };
    const scrollFor = i => { const g = geo(); const it = vis[clamp(i, 0, vis.length - 1)]; return it ? clamp((it.el.offsetLeft - g.pad) / (1 + g.b / g.Mx), 0, g.Mx) : 0; };
    function paint() {
      raf = 0;
      if (!vis.length) { win.style.left = '0%'; win.style.width = '100%'; return; }
      const g = geo(), s = track.scrollLeft;
      const target = hoverIt && !hoverIt.el.hidden ? hoverIt.el.offsetLeft + g.iw / 2 : focalOf(s);
      focal = focal == null || RM() ? target : focal + (target - focal) * .28;
      if (Math.abs(target - focal) > .5) raf = requestAnimationFrame(paint);
      const lo = s - g.iw * 2, hi = s + g.vw + g.iw * 2;
      let best = 0, bd = 1e9;
      vis.forEach((it, i) => {
        const c = it.el.offsetLeft + g.iw / 2, d = Math.abs(c - focal) / g.iw;
        if (d < bd) { bd = d; best = i; }
        if (c < lo || c > hi) { if (it.k !== .86) { it.plate.style.setProperty('--k', .86); it.k = .86; } return; }
        const k = .86 + .14 * (1 - sstep(0, 1.4, d));
        if (Math.abs((it.k || 0) - k) > .001) { it.plate.style.setProperty('--k', k.toFixed(4)); it.k = k; }
      });
      focus = best;
      const sw = g.pad * 2 + vis.length * g.iw;
      win.style.left = (clamp(s / sw) * 100) + '%'; win.style.width = (clamp(g.vw / sw) * 100) + '%';
    }
    const kick = () => { if (!raf) raf = requestAnimationFrame(paint); };
    function stopAnim() { if (anim) { cancelAnimationFrame(anim.id); anim = null; } }
    function animTo(target, dur = 520) {
      stopAnim(); const s0 = track.scrollLeft, t0 = performance.now();
      if (RM() || Math.abs(target - s0) < 1) { track.scrollLeft = target; kick(); return; }
      anim = { id: 0 };
      const step = t => { const p = clamp((t - t0) / dur); track.scrollLeft = s0 + (target - s0) * expo(p); if (p < 1) anim.id = requestAnimationFrame(step); else anim = null; };
      anim.id = requestAnimationFrame(step);
    }
    const goTo = i => { i = clamp(i, 0, vis.length - 1); animTo(scrollFor(i), 560); return i; };
    const nearest = () => { const g = geo(), f = focalOf(track.scrollLeft); let b = 0, bd = 1e9; vis.forEach((it, i) => { const d = Math.abs(it.el.offsetLeft + g.iw / 2 - f); if (d < bd) { bd = d; b = i; } }); return b; };

    /* ---- drag with inertia (mouse), native scrolling for touch ---- */
    track.addEventListener('scroll', kick, { passive: true });
    let drag = null;
    track.addEventListener('pointerdown', e => { if (e.pointerType !== 'mouse' || e.button !== 0) return; stopAnim(); drag = { x: e.clientX, s: track.scrollLeft, moved: 0, v: 0, lt: performance.now(), lx: e.clientX, id: e.pointerId }; });
    track.addEventListener('pointermove', e => {
      if (!drag || e.pointerId !== drag.id) return;
      const dx = e.clientX - drag.x;
      if (!drag.moved && Math.abs(dx) > 5) { drag.moved = 1; track.classList.add('drag'); try { track.setPointerCapture(drag.id); } catch (er) {} hoverIt = null; }
      if (drag.moved) { track.scrollLeft = drag.s - dx; const t = performance.now(), dt = Math.max(1, t - drag.lt); drag.v = drag.v * .6 + (-(e.clientX - drag.lx) / dt) * .4; drag.lt = t; drag.lx = e.clientX; }
    });
    const endDrag = () => {
      if (!drag) return; const d = drag; drag = null; track.classList.remove('drag');
      if (!d.moved) return;
      suppress = true; setTimeout(() => { suppress = false; }, 60);
      if (RM()) { track.scrollLeft = scrollFor(nearest()); return; }
      let v = clamp(d.v, -4, 4), lt = performance.now(); anim = { id: 0 };
      const step = t => { const dt = t - lt; lt = t; track.scrollLeft += v * dt; v *= Math.pow(.94, dt / 16);
        if (Math.abs(v) > .03 && track.scrollLeft > 0 && track.scrollLeft < geo().Mx) anim.id = requestAnimationFrame(step); else { anim = null; animTo(scrollFor(nearest()), 380); } };
      anim.id = requestAnimationFrame(step);
    };
    track.addEventListener('pointerup', endDrag); track.addEventListener('pointercancel', endDrag);
    track.addEventListener('click', e => { if (suppress) { e.preventDefault(); e.stopPropagation(); } }, true);
    track.addEventListener('wheel', stopAnim, { passive: true });
    track.addEventListener('touchstart', stopAnim, { passive: true });
    track.addEventListener('dragstart', e => e.preventDefault());
    if (matchMedia('(hover:hover) and (pointer:fine)').matches) {
      track.addEventListener('pointerover', e => { if (drag || e.pointerType !== 'mouse') return; const a = e.target.closest('.go-it'); const it = a && items.find(x => x.el === a); if (it) { hoverIt = it; kick(); } });
      track.addEventListener('pointerleave', () => { hoverIt = null; kick(); });
    }
    const say = it => { const k = $('.go-k', it.el), pr = $('.go-pr', it.el); live.textContent = `${k ? k.textContent : ''}, ${pr ? pr.textContent.replace(/\s+/g, ' ').trim() : ''}`; };
    track.addEventListener('keydown', e => {
      const k = e.key; let i = null;
      // from the link that has keyboard focus when there is one (Tab lands on any card), else from the focal boot
      const ae = document.activeElement, onCard = !!ae && ae.classList.contains('go-it') && track.contains(ae);
      const j = onCard ? vis.findIndex(it => it.el === ae) : -1, from = j >= 0 ? j : focus;
      if (k === 'ArrowRight') i = from + 1; else if (k === 'ArrowLeft') i = from - 1; else if (k === 'Home') i = 0; else if (k === 'End') i = vis.length - 1;
      if (i == null || !vis.length) return; e.preventDefault(); hoverIt = null; i = goTo(i); focus = i;
      say(vis[i]);
      if (onCard) vis[i].el.focus({ preventScroll: true });
    });

    /* ---- the indicator: click to travel, drag to scrub ---- */
    let id0 = null;
    const at = e => { const r = ind.getBoundingClientRect(); return clamp((e.clientX - r.left) / r.width); };
    const scrubTo = f => { const g = geo(); track.scrollLeft = clamp(f * (g.pad * 2 + vis.length * g.iw) - g.vw / 2, 0, g.Mx); };
    ind.addEventListener('pointerdown', e => { stopAnim(); hoverIt = null; id0 = { x: e.clientX, scrub: false }; try { ind.setPointerCapture(e.pointerId); } catch (er) {} const g = geo(); animTo(clamp(at(e) * (g.pad * 2 + vis.length * g.iw) - g.vw / 2, 0, g.Mx), 520); });
    ind.addEventListener('pointermove', e => { if (id0 && (id0.scrub || Math.abs(e.clientX - id0.x) > 4)) { id0.scrub = true; stopAnim(); scrubTo(at(e)); } });
    const up = () => { if (id0) { const sc = id0.scrub; id0 = null; if (sc) animTo(scrollFor(nearest()), 320); } };
    ind.addEventListener('pointerup', up); ind.addEventListener('pointercancel', up);

    /* ---- the two questions ---- */
    const placeInk = () => groups.forEach(g => { const on = $('.go-way[aria-checked="true"]', g), ink = $('.go-ink', g); if (!on || !ink) return; ink.style.width = on.offsetWidth + 'px'; ink.style.transform = `translateX(${on.offsetLeft}px)`; });
    const summary = s => SUMS[`${s.disc}|${s.fin}`] || sum.textContent;
    const hashFor = s => { const b = BR.find(x => x.tag === s.disc), k = b ? b.key : ''; return s.fin === DEC ? '#' + (k ? k + '-' : '') + DEC : (k ? '#' + k : ''); };
    let dead = false; // set by destroy(): a section the editor has unloaded never touches the URL or the page's state again
    const writeHash = () => {
      if (dead) return;
      const h = hashFor(state);
      if (h === location.hash || (!h && (!location.hash || location.hash === '#models'))) return;
      try { history.replaceState(history.state, '', location.pathname + location.search + h); } catch (e) {}
    };
    let said = ''; // a change that comes back to the announced state inside a fade re-hangs, but tells no one twice
    // fromHistory: the state was RESTORED by Back / Forward, not chosen — listeners must not treat it as a new choice
    // (the index would rewrite the entry the reader has returned to)
    const announce = (init, fromHistory) => {
      if (dead) return;
      const key = state.disc + '|' + state.fin;
      if (!init && key === said) return;
      said = key; last = api;
      document.dispatchEvent(new CustomEvent('rbc:gallery:state', { detail: { disc: state.disc, fin: state.fin, init: !!init, fromHistory: !!fromHistory } }));
    };
    const checkTabs = s => groups.forEach(g => { const v = g.dataset.axis === 'fin' ? s.fin : s.disc; $$('.go-way', g).forEach(b => { const on = b.dataset.v === v; b.setAttribute('aria-checked', on); b.tabIndex = on ? 0 : -1; }); });
    // the boot the reader is looking at, or the nearest one that survives the change
    const anchorFor = next => {
      const f = vis[focus] || vis[0];
      if (f && want(f, next)) return f;
      const fi = f ? items.indexOf(f) : 0; let a = null, bd = 1e9;
      items.forEach((it, i) => { if (want(it, next)) { const d = Math.abs(i - fi) + (i < fi ? .5 : 0); if (d < bd) { bd = d; a = it; } } });
      return a;
    };
    // intent (hover / focus / press on a tab): start the photographs the change would hang in view
    const warm = next => {
      const list = items.filter(it => want(it, next)), a = anchorFor(next), i = Math.max(0, list.indexOf(a));
      list.slice(Math.max(0, i - 2), i + 10).forEach(it => { if (it.img && it.img.loading === 'lazy') it.img.loading = 'eager'; });
    };
    const norm = (d, cur) => {
      const n = { disc: cur.disc, fin: cur.fin };
      if (d && 'disc' in d) { const v = String(d.disc == null ? '' : d.disc); if (v === '' || v.toLowerCase() === 'all') n.disc = ''; else { const b = BR.find(x => x.tag.toLowerCase() === v.toLowerCase() || x.key === v.toLowerCase()); if (b) n.disc = b.tag; } }
      if (d && 'fin' in d && hasFin) n.fin = d.fin === DEC || d.fin === 'dec' ? DEC : PLAIN;
      return n;
    };

    /* setState: re-hang the wall. Race-safe as in the prototype: a change that lands inside the fade-out of the one before
       wins (gen counter); the stale settle steps aside; fades are awaited with allSettled (cancelled animations reject). */
    function setState(d, opt = {}) {
      if (dead) return false;
      const next = norm(d, state);
      if (next.disc === state.disc && next.fin === state.fin) return false;
      const animate = !RM() && !opt.instant;
      checkTabs(next); placeInk();
      // a change inside the previous one's entrance: let those animations go to their end before measuring
      entering.forEach(a => { try { a.cancel(); } catch (e) {} }); entering = [];
      const tr = track.getBoundingClientRect();
      const inView = it => { const r = it.el.getBoundingClientRect(); return r.right > tr.left && r.left < tr.right; };
      const before = new Map(); vis.forEach(it => { if (inView(it)) before.set(it, it.el.getBoundingClientRect().left - tr.left); });
      const anchor = anchorFor(next);
      const ax = anchor && before.has(anchor) ? before.get(anchor) : null;
      const leaving = vis.filter(it => !want(it, next) && before.has(it));
      Object.assign(state, next);
      const settle = () => {
        fading.forEach(a => { try { a.cancel(); } catch (e) {} }); fading = [];
        items.forEach(it => { it.el.hidden = !want(it); });
        track.classList.toggle('dec', state.fin === DEC);
        vis = items.filter(it => want(it));
        const g = geo();
        track.scrollLeft = !anchor ? 0 : ax != null ? clamp(anchor.el.offsetLeft - ax, 0, g.Mx) : scrollFor(vis.indexOf(anchor));
        focal = null; hoverIt = null; paint();
        const text = summary(state);
        if (!opt.silent) { if (!opt.history) writeHash(); live.textContent = text; announce(false, opt.history); }
        if (!animate) { sum.textContent = text; return; }
        sum.classList.add('out'); setTimeout(() => { sum.textContent = text; sum.classList.remove('out'); }, 150);
        const tr2 = track.getBoundingClientRect(); let k = 0;
        vis.forEach(it => {
          const r = it.el.getBoundingClientRect(); if (!(r.right > tr2.left && r.left < tr2.right)) return;
          if (before.has(it)) { const dx = before.get(it) - (r.left - tr2.left); if (Math.abs(dx) > 1) entering.push(it.el.animate([{ transform: `translateX(${dx}px)` }, { transform: 'none' }], { duration: 760, easing: 'cubic-bezier(.19,1,.22,1)' })); }
          else entering.push(it.el.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 600, delay: 100 + (k++) * 50, easing: 'cubic-bezier(.33,0,0,1)', fill: 'backwards' }));
        });
        entering.forEach(a => a.finished.catch(() => {}));
      };
      const my = ++gen; // a second change inside the fade-out wins; the stale settle steps aside
      // fades in flight from a superseded change: boots still leaving keep fading; the ones that now stay come straight back
      const keep = new Set(leaving.map(it => it.el));
      const carried = new Map();
      fading.forEach(a => { const el = a.effect && a.effect.target; if (el && keep.has(el) && a.playState !== 'idle') carried.set(el, a); else { try { a.cancel(); } catch (e) {} } });
      if (animate && leaving.length) {
        fading = leaving.map(it => carried.get(it.el) || it.el.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 240, easing: 'cubic-bezier(.4,0,1,1)', fill: 'forwards' }));
        Promise.allSettled(fading.map(a => a.finished.catch(() => {}))).then(() => { if (my === gen) settle(); });
      } else { fading.forEach(a => { try { a.cancel(); } catch (e) {} }); fading = []; settle(); }
      return true;
    }

    groups.forEach(g => {
      const axis = g.dataset.axis, bs = $$('.go-way', g), ks = bs.map(b => b.dataset.v);
      bs.forEach((b, i) => {
        b.addEventListener('click', () => setState({ [axis]: b.dataset.v }));
        b.addEventListener('pointerenter', () => warm(norm({ [axis]: b.dataset.v }, state)));
        b.addEventListener('pointerdown', () => warm(norm({ [axis]: b.dataset.v }, state)));
        b.addEventListener('focus', () => { const j = (i + 1) % bs.length; warm(norm({ [axis]: ks[j] }, state)); });
        b.addEventListener('keydown', e => {
          if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft' && e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
          e.preventDefault();
          const cur = ks.indexOf(axis === 'fin' ? state.fin : state.disc), j = (cur + (e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : ks.length - 1)) % ks.length;
          setState({ [axis]: ks[j] }); bs[j].focus();
        });
      });
    });

    /* ---- the fork and the deep links: they all land on the wall, already re-hung ---- */
    const toWall = smooth => wall.scrollIntoView({ behavior: smooth && !RM() ? 'smooth' : 'instant', block: 'start' });
    $$('.go-fk, .go-all', root).forEach(a => a.addEventListener('click', e => {
      if (e.defaultPrevented || e.button || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault(); setState({ disc: a.dataset.disc || '', fin: PLAIN }); toWall(true);
    }));
    const parseHash = h => {
      try { h = decodeURIComponent((h || '').replace(/^#/, '')).toLowerCase(); } catch (e) { return null; }
      if (!h) return null;
      if (h === 'models') return { disc: '', fin: PLAIN };
      if (h === DEC) return hasFin ? { disc: '', fin: DEC } : null;
      let fin = PLAIN;
      if (h.endsWith('-' + DEC)) { if (!hasFin) return null; fin = DEC; h = h.slice(0, -DEC.length - 1); }
      const b = BR.find(x => x.key === h); return b ? { disc: b.tag, fin } : null;
    };
    // The state a URL names: disc from the grid's filter parameter if present, else the hash; fin from the hash.
    const stateFromUrl = () => {
      const h = parseHash(location.hash);
      const st = { disc: h ? h.disc : '', fin: h ? h.fin : PLAIN };
      const q = new URLSearchParams(location.search).getAll(FILTER);
      if (q.length) { const m = q.length === 1 && BR.find(x => x.tag.toLowerCase() === q[0].toLowerCase()); st.disc = m ? m.tag : ''; }
      return st;
    };
    // an in-page link to another wall state re-hangs and lands on the wall; a hash that names the state already shown
    // (Back restoring #dressage after a grid action) changes nothing and moves no one
    addEventListener('hashchange', () => {
      // no hash at all is a state too (All / Plain): Back to an entry without one must take the wall there
      const s = location.hash ? parseHash(location.hash) : { disc: '', fin: PLAIN };
      if (s && (s.disc !== state.disc || s.fin !== state.fin)) { setState(s); if (location.hash) toWall(true); }
    }, sig);
    document.addEventListener('rbc:gallery:set', e => setState(e.detail || {}), sig);
    // the grid's own sort / filter / page actions rewrite the URL without the fragment (the provider's default); the hash
    // is the wall's, so put it back each time the grid swaps. Never preventDefault: the provider does the swap.
    // Back / Forward. `hashchange` only fires when nothing but the fragment differs between two entries; after a grid
    // action the query differs too, so the wall reads the entry it has landed on itself. No scroll: the browser
    // restores the reader's position.
    addEventListener('popstate', () => {
      if (dead) return;
      const s = stateFromUrl();
      if (s.disc !== state.disc || s.fin !== state.fin) setState(s, { history: true });   // restore; the URL is already right
    }, sig);
    document.addEventListener('collection:swap', e => {
      if (dead) return;
      // Back / Forward: the entry the reader returned to carries its own hash, and it wins (hashchange re-hangs the
      // wall to it). Re-asserting the current state here would overwrite that entry.
      if (e.detail && e.detail.fromHistory) return;
      const h = hashFor(state);
      if (!h || location.hash === h) return;
      try { history.replaceState(history.state, '', location.pathname + location.search + h); } catch (e) {}
    }, sig);
    document.addEventListener('shopify:block:select', e => { const fk = e.target && e.target.closest && e.target.closest('.go-fk'); if (fk && root.contains(fk)) setState({ disc: fk.dataset.disc, fin: PLAIN }); }, sig);

    const layout = () => { const i = focus; placeInk(); track.scrollLeft = vis.length ? scrollFor(i) : 0; focal = null; paint(); };
    let lastW = innerWidth, rq = 0;
    addEventListener('resize', () => { cancelAnimationFrame(rq); rq = requestAnimationFrame(() => { if (innerWidth !== lastW) { lastW = innerWidth; layout(); } }); }, sig);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => { if (!ac.signal.aborted) { placeInk(); paint(); } });

    /* ---- first state: disc from the grid's filter parameter if present, else the hash; fin from the hash ---- */
    const fromHash = parseHash(location.hash);
    const first = stateFromUrl();
    setState(first, { instant: true, silent: true });
    // a wall hash that disagrees with the state it produced (?…category=Jumping#dressage, #DRESSAGE) is rewritten to match
    if (fromHash && location.hash.toLowerCase() !== '#models' && location.hash !== hashFor(state)) writeHash();
    checkTabs(state); placeInk(); paint();
    // the #jumping / #dressage… anchor spans are for pages without script. With script they must go: Chrome scrolls a
    // history traversal to its fragment's target (Back after a grid action threw the reader ~2,800px up to the wall)
    $$('.go-anchor[id]', root).forEach(a => a.removeAttribute('id'));

    /* Back from a product page (or a reload) returns to the boot the rider was on, not the start of the row. Kept per URL
       (search + hash, which carry the state) in sessionStorage; restored without animation. bfcache restores need nothing. */
    const SKEY = 'rbc-go:' + location.pathname;
    const remember = () => {
      if (dead || !vis.length) return;
      const it = vis[focus] || vis[0];
      try { sessionStorage.setItem(SKEY, JSON.stringify({ key: location.search + location.hash, i: focus, href: it.el.getAttribute('href'), x: Math.round(track.scrollLeft), w: track.clientWidth })); } catch (e) {}
    };
    track.addEventListener('click', e => { if (e.target.closest('.go-it')) remember(); });
    addEventListener('pagehide', remember, sig);
    if (/^(back_forward|reload)$/.test(nav().type || '')) {
      let saved = null;
      try { saved = JSON.parse(sessionStorage.getItem(SKEY) || 'null'); } catch (e) {}
      if (saved && saved.key === location.search + location.hash && vis.length) {
        let i = saved.href ? vis.findIndex(it => it.el.getAttribute('href') === saved.href) : -1;
        if (i < 0) i = clamp(saved.i | 0, 0, vis.length - 1);
        track.scrollLeft = saved.w === track.clientWidth && Number.isFinite(saved.x) ? saved.x : scrollFor(i);
        focal = null; paint();
      }
    }
    root.classList.add('is-ready');
    requestAnimationFrame(() => requestAnimationFrame(() => root.classList.add('is-live')));
    // a deep link (#dressage…) lands on the wall; a reload or Back keeps the reader where the browser puts them
    if (fromHash && location.hash.toLowerCase() !== '#models' && (nav().type || 'navigate') === 'navigate') toWall(false);

    const api = {
      root,
      get state() { return { disc: state.disc, fin: state.fin }; },
      set: d => setState(d),
      layout,
      destroy() {
        dead = true; gen++; // any settle still waiting on a fade finds itself stale and steps aside
        ac.abort(); stopAnim(); cancelAnimationFrame(raf); cancelAnimationFrame(rq);
        fading.concat(entering).forEach(a => { try { a.cancel(); } catch (e) {} }); fading = []; entering = [];
      }
    };
    last = api;
    if (dclDone()) announce(true); else document.addEventListener('DOMContentLoaded', () => announce(true), { once: true, signal: ac.signal });
    return api;
  }

  /* ?page=2 hard loads: `paginate` read the page parameter and the server rendered no boots. Fetch this section as it is on
     page 1 (Section Rendering API) and put it in place, so the opening and the wall are never blank. */
  async function recover(root) {
    if (root.__rbcRecovering) return; root.__rbcRecovering = true;
    try {
      const res = await fetch(root.dataset.recover, { credentials: 'same-origin', headers: { Accept: 'text/html' } });
      if (!res.ok) throw new Error(res.status);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const fresh = doc.querySelector('.rbc-g-open');
      if (!fresh || fresh.dataset.recover || !root.isConnected) throw new Error('no wall');
      const ld = doc.querySelector('script[type="application/ld+json"]');
      root.replaceWith(fresh);
      if (ld) { const s = document.createElement('script'); s.type = 'application/ld+json'; s.textContent = ld.textContent; fresh.after(s); }
      mount(fresh);
    } catch (e) { root.__rbcRecovering = false; root.classList.remove('is-recovering'); }
  }

  function mount(root) {
    if (walls.has(root)) return;
    if (root.dataset.recover) { recover(root); return; }
    walls.set(root, Wall(root));
  }
  function unmount(root) { const w = walls.get(root); if (w) { w.destroy(); walls.delete(root); if (last === w) last = null; } }
  function boot(scope = document) { $$('.rbc-g-open', scope).forEach(mount); }

  document.addEventListener('shopify:section:load', e => boot(e.target));
  document.addEventListener('shopify:section:unload', e => $$('.rbc-g-open', e.target).forEach(unmount));
  window.rbcGalleryOpen = { boot, get state() { return last ? last.state : null; } };
  boot();
})();
